import { toast } from "react-toastify";

import { getFieldErrorMessage } from "@/components/Forms/form.context";

type GroupSubmitInvalidArgs = {
  groupApi?: {
    getRelatedFields?: () => Array<{
      name: string;
      state: { meta: { errors: unknown[]; isTouched?: boolean } };
    }>;
    state?: { meta?: { errors?: unknown[] } };
  };
};

/**
 * Scrolls smoothly to the first invalid field in the current FormGroup and focuses it.
 *
 * Uses `requestAnimationFrame` so it runs after TanStack Form has finished
 * marking fields invalid and MUI has applied `.Mui-error` classes to the DOM.
 *
 * If no visible error exists (e.g. client mandataire fields are `display: none`),
 * surfaces a toast so submit failures are never silent.
 *
 * Usage:
 *   <form.FormGroup onGroupSubmitInvalid={scrollToFirstError} />
 */
export function scrollToFirstError(args?: GroupSubmitInvalidArgs): void {
  requestAnimationFrame(() => {
    const firstError = document.querySelector<HTMLElement>(
      "[aria-invalid='true'], .Mui-error input, .Mui-error textarea"
    );

    if (firstError) {
      // Skip focusing elements inside visually hidden containers
      const hidden = firstError.closest("[aria-hidden='true']");
      if (!hidden) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        firstError.focus({ preventScroll: true });
        return;
      }
    }

    const fieldMessages =
      args?.groupApi?.getRelatedFields?.().flatMap((field) => {
        const message = getFieldErrorMessage(field.state.meta.errors);
        return message ? [`${field.name}: ${message}`] : [];
      }) ?? [];

    const groupMessages =
      args?.groupApi?.state?.meta?.errors
        ?.map((err) => getFieldErrorMessage([err]))
        .filter((msg): msg is string => Boolean(msg)) ?? [];

    const messages = [...fieldMessages, ...groupMessages];
    if (messages.length === 0) {
      toast.error("Certains champs sont invalides. Vérifiez le formulaire.");
      return;
    }

    toast.error(messages.slice(0, 4).join("\n"), {
      style: { whiteSpace: "pre-line" },
    });
  });
}
